/**
 * Rahmen jeder Seite der Sektion „Anfos & Umsatz": Überschrift, ein Satz, was die Seite beantwortet,
 * und der echte Datenstand je Quelle (nicht die Uhrzeit des Aufrufs). Der Datenstand steht den
 * Karten darunter über `useAuswertungenMeta()` zur Verfügung, damit jede Karte ihr „Stand" zeigt,
 * ohne ihn selbst zu laden.
 */
import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../Header';
import InfoTip from '../InfoTip';
import {
  AuswertungenMeta,
  fetchAuswertungenMeta,
  formatBerlinWallclock,
} from '../../helpers/auswertungenData';

const MetaContext = createContext<AuswertungenMeta | null>(null);

export const useAuswertungenMeta = (): AuswertungenMeta | null => useContext(MetaContext);

interface ChapterPageProps {
  title: string;
  /** Ein Satz: welche Frage beantwortet diese Seite? */
  question: string;
  intro?: ReactNode;
  /** Unterseite: Link zurück zum Kapitel über der Überschrift. */
  zurueck?: { to: string; label: string };
  /**
   * Nur diese Quellen in der Datenstandzeile ausschreiben, die übrigen stehen im ⓘ daneben (P3-Review:
   * sechs Systemnamen in einer Zeile las niemand). Ohne Angabe wie bisher alle.
   */
  standQuellen?: { key: string; label?: string }[];
  children: ReactNode;
}

const ChapterPage: FC<ChapterPageProps> = ({ title, question, intro, zurueck, standQuellen, children }) => {
  const [meta, setMeta] = useState<AuswertungenMeta | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAuswertungenMeta()
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <MetaContext.Provider value={meta}>
      <Box m="20px" maxWidth={1400}>
        {zurueck && (
          <Link component={RouterLink} to={zurueck.to} underline="hover" sx={{ display: 'inline-block', mb: 1, fontSize: 14 }}>
            ← {zurueck.label}
          </Link>
        )}
        <Header title={title} subtitle={question} />
        {intro && (
          <Typography variant="body1" sx={{ mt: -2, mb: 2, maxWidth: '75ch', lineHeight: 1.6 }}>
            {intro}
          </Typography>
        )}
        {fehler && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Datenstand konnte nicht geladen werden: {fehler}
          </Alert>
        )}
        {meta && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 3,
              rowGap: 0.5,
              mb: 3,
              color: 'text.secondary',
              fontSize: 13,
            }}
          >
            {standQuellen ? (
              <>
                {standQuellen.map((q) => {
                  const s = meta.sources.find((x) => x.key === q.key);
                  return s ? (
                    <span key={s.key} title={s.note}>
                      {q.label ?? s.label}: Stand {formatBerlinWallclock(s.updated_at)}
                    </span>
                  ) : null;
                })}
                {meta.sources.some((s) => !standQuellen.some((q) => q.key === s.key)) && (
                  <InfoTip
                    text={meta.sources
                      .filter((s) => !standQuellen.some((q) => q.key === s.key))
                      .map((s) => `${s.label}: Stand ${formatBerlinWallclock(s.updated_at)}`)
                      .join(' · ')}
                  />
                )}
              </>
            ) : (
              meta.sources.map((s) => (
                <span key={s.key} title={s.note}>
                  {s.label}: Stand {formatBerlinWallclock(s.updated_at)}
                </span>
              ))
            )}
          </Box>
        )}
        {children}
      </Box>
    </MetaContext.Provider>
  );
};

export default ChapterPage;
